import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';

// Store
import { Dispatch } from '../../store/store';

// Utils
import generateIdDevice from '../../utils/generateIdDevice';
import { extractChannelNumberFromId } from '../../utils/channel-utils';

// Enums
import { ChangeChannelAction } from '../../enums/action';

// Components
import Modal from '../Modal/Modal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import CheckboxCircleControl from '../CheckboxCircleControl/CheckboxCircleControl';
import { toast } from 'react-toastify';

// Types
import { ButtonType } from '../../types/button-type';
import { ElementSize } from '../../types/element-size';
import { CycleController } from '../../store/models/controllers';
import { ChannelActionItem } from '../../store/models/channels';

type ComponentProps = {
  isVisible: boolean;
  onClose: () => void;
  cycleId: string;
  cycleControllers: CycleController[];
  deviceNames: { [key: string]: string };
};

type DispatchProps = ReturnType<typeof mapDispatch>;

type ChannelQuickSetProps = ComponentProps & DispatchProps;

type ValueType = 'power' | 'trim';

const ChannelQuickSet: React.FC<ChannelQuickSetProps> = ({
  isVisible,
  onClose,
  cycleId,
  cycleControllers,
  deviceNames,
  updateChannels,
}) => {
  const [powerValue, setPowerValue] = useState<string>('');
  const [trimValue, setTrimValue] = useState<string>('');
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
  const [applyingType, setApplyingType] = useState<ValueType | null>(null);

  // Validation limits (aligned with channelActions limits)
  const POWER_MIN = 0;
  const POWER_MAX = 100;
  const TRIM_MIN = -1500;
  const TRIM_MAX = 1500;

  // Get all available channels from cycle controllers
  const availableChannels = useMemo(() => {
    const channels: Array<{ id: string; name: string; controllerId: string; channelNum: number | null; channelRaw: string }> = [];
    
    cycleControllers.forEach((cycleController) => {
      const serialNumber = cycleController.controller.serial_number;
      
      cycleController.channels.forEach((channel) => {
        const channelRaw = channel !== undefined && channel !== null ? String(channel) : '';
        if (!channelRaw) return; // skip invalid entries
        
        // Generate proper device ID (e.g., "000T29YM#C-5")
        const idDevice = generateIdDevice(serialNumber, channelRaw);
        const deviceName = deviceNames[idDevice] || `Channel ${channelRaw}`;
        
        // Extract numeric channel number using the existing helper
        const channelNum = extractChannelNumberFromId(idDevice);
        if (channelNum === null) return; // skip channels we can't extract number from

        channels.push({
          id: idDevice, // use full device ID for selection
          name: deviceName,
          controllerId: serialNumber,
          channelNum,
          channelRaw,
        });
      });
    });
    
    return channels;
  }, [cycleControllers, deviceNames]);

  const toggleChannel = (channelId: string) => {
    const newSelected = new Set(selectedChannels);
    if (newSelected.has(channelId)) {
      newSelected.delete(channelId);
    } else {
      newSelected.add(channelId);
    }
    setSelectedChannels(newSelected);
  };

  const selectAll = () => {
    const allChannelIds = new Set(availableChannels.map((ch) => ch.id));
    setSelectedChannels(allChannelIds);
  };

  const selectNone = () => {
    setSelectedChannels(new Set());
  };

  const applyValues = async (type: ValueType) => {
    if (selectedChannels.size === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    const value = type === 'power' ? powerValue : trimValue;
    if (value === '' || value === null || value === undefined) {
      toast.error(`Please enter a value for ${type === 'power' ? 'power' : 'trim'}`);
      return;
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      toast.error('Value must be a number');
      return;
    }

    if (type === 'power' && (numeric < POWER_MIN || numeric > POWER_MAX)) {
      toast.error(`Power must be between ${POWER_MIN} and ${POWER_MAX}`);
      return;
    }

    if (type === 'trim' && (numeric < TRIM_MIN || numeric > TRIM_MAX)) {
      toast.error(`Trim must be between ${TRIM_MIN} and ${TRIM_MAX}`);
      return;
    }

    setApplyingType(type);

    try {
      const action = type === 'power'
        ? ChangeChannelAction.SET_CHANNEL_POWER_SCALE
        : ChangeChannelAction.SET_CHANNEL_TRIM_CHANNEL_SETPOINT;

      const channelList: ChannelActionItem[] = availableChannels
        .filter((ch) => selectedChannels.has(ch.id))
        .filter((ch) => ch.channelNum !== null) // ensure valid channel number
        .map((ch) => ({
          controller_serial_number: ch.controllerId,
          channel_num: String(ch.channelNum), // use extracted numeric channel
          value: value,
        }));

      if (channelList.length === 0) {
        toast.error('No valid channels selected');
        return;
      }

      await updateChannels({
        cycleId,
        data: {
          action,
          list: channelList,
        },
      });

      // Reset after successful application
      setSelectedChannels(new Set());
      onClose();
    } catch (error) {
      console.error('Error applying channel values:', error);
    } finally {
      setApplyingType(null);
    }
  };

  const isPowerValid = () => {
    if (!powerValue) return false;
    const v = Number(powerValue);
    return !Number.isNaN(v) && v >= POWER_MIN && v <= POWER_MAX;
  };

  const isTrimValid = () => {
    if (!trimValue) return false;
    const v = Number(trimValue);
    return !Number.isNaN(v) && v >= TRIM_MIN && v <= TRIM_MAX;
  };

  const handleClose = () => {
    setSelectedChannels(new Set());
    setPowerValue('');
    setTrimValue('');
    onClose();
  };

  return (
    <Modal
      modalTitle="Channel Quick Set"
      isVisible={isVisible}
      onClose={handleClose}
      className="min-h-[500px] w-[600px]"
      showCancelBtn={false}
      showAcceptBtn={false}
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Power and Trim Input Fields */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-2 flex-1">
            <label className="text-sm font-medium text-slate-gray dark:text-white">
              P-Lim
            </label>
            <input
              type="number"
              value={powerValue}
              placeholder="Type a value"
              onChange={(e) => setPowerValue(e.target.value)}
              className="w-full rounded border border-foggy-mist bg-white px-3 py-2 text-sm text-black outline-none focus:border-red dark:border-strokedark dark:bg-boxdark dark:text-white"
              min="0"
              max="100"
            />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <label className="text-sm font-medium text-slate-gray dark:text-white">
              Trim
            </label>
            <input
              type="number"
              value={trimValue}
              placeholder="Type a value"
              onChange={(e) => setTrimValue(e.target.value)}
              className="w-full rounded border border-foggy-mist bg-white px-3 py-2 text-sm text-black outline-none focus:border-red dark:border-strokedark dark:bg-boxdark dark:text-white"
              min="-1500"
              max="1500"
            />
          </div>
        </div>

        {/* Channel Selection */}
        <div className="flex flex-col gap-2 rounded-lg border-2 border-red p-4">
          <div className="mb-2 text-sm font-medium text-slate-gray dark:text-white">
            Select Channels
          </div>
          
          <div className="mb-3 flex flex-col gap-2 max-h-56 overflow-auto">
            {availableChannels.map((channel) => (
              <div key={channel.id} className="flex items-center gap-2">
                <CheckboxCircleControl
                  id={channel.id}
                  name={channel.id}
                  checked={selectedChannels.has(channel.id)}
                  onChange={() => toggleChannel(channel.id)}
                  label={channel.name}
                />
              </div>
            ))}
          </div>

          {/* Select All / None Buttons */}
          <div className="flex gap-2">
            <InteractiveButton
              size={ElementSize.SM}
              type={ButtonType.Secondary}
              text="None"
              onClick={selectNone}
              className="flex-1"
            />
            <InteractiveButton
              size={ElementSize.SM}
              type={ButtonType.Primary}
              text="All"
              onClick={selectAll}
              className="flex-1"
            />
          </div>
        </div>

        {/* Apply Buttons */}
        <div className="flex gap-2">
          <InteractiveButton
            size={ElementSize.MD}
            type={ButtonType.Primary}
            text={applyingType === 'power' ? 'Applying...' : 'Write Powers'}
            onClick={() => applyValues('power')}
            disabled={!isPowerValid() || selectedChannels.size === 0 || applyingType !== null}
            className="flex-1"
          />
          <InteractiveButton
            size={ElementSize.MD}
            type={ButtonType.Primary}
            text={applyingType === 'trim' ? 'Applying...' : 'Write Trims'}
            onClick={() => applyValues('trim')}
            disabled={!isTrimValid() || selectedChannels.size === 0 || applyingType !== null}
            className="flex-1"
          />
        </div>
      </div>
    </Modal>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  updateChannels: dispatch.channels.updateChannels,
});

export default connect<Record<string, never>, DispatchProps, ComponentProps>(null, mapDispatch)(ChannelQuickSet);
